import { useState, type FormEvent } from 'react';
import { company } from '@/mocks/site';
import { trainingPrograms } from '@/mocks/formation';
import { buildWhatsAppUrl } from '@/hooks/whatsappLink';

const FORM_ENDPOINT = 'https://readdy.ai/api/form/dblqk713ild7mhsptot0';

type RegistrationFormProps = {
  selectedProgram: string;
  onProgramChange: (value: string) => void;
};

export default function RegistrationForm({
  selectedProgram,
  onProgramChange,
}: RegistrationFormProps) {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [waLink, setWaLink] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get('contact_alt') ?? '').trim();

    if (honeypot) {
      setStatus('success');
      return;
    }

    const fullName = String(formData.get('full_name') ?? '').trim();
    const email = String(formData.get('email') ?? '').trim();
    const phone = String(formData.get('phone') ?? '').trim();
    const organisation = String(formData.get('organisation') ?? '').trim();
    const experience = String(formData.get('experience') ?? '').trim();
    const message = String(formData.get('message') ?? '').trim();
    const program = String(formData.get('program') ?? '').trim();

    if (!fullName) {
      setStatus('error');
      setFormError('Merci de renseigner votre nom complet.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      setFormError('Merci de saisir une adresse e-mail valide.');
      return;
    }
    if (!program) {
      setStatus('error');
      setFormError('Merci de sélectionner le parcours de formation souhaité.');
      return;
    }
    if (message.length > 500) {
      setStatus('error');
      setFormError('Merci de limiter votre message à 500 caractères.');
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'contact_alt') return;
      const text = String(value).trim();
      if (text) payload.append(key, text);
    });

    const summary = [
      'Nouvelle inscription formation — SGSCTIC',
      '',
      `Nom complet : ${fullName}`,
      `E-mail : ${email}`,
      phone ? `Téléphone : ${phone}` : '',
      organisation ? `Entreprise : ${organisation}` : '',
      `Parcours souhaité : ${program}`,
      experience ? `Niveau actuel : ${experience}` : '',
      message ? `Projet de formation : ${message}` : '',
    ].filter((line) => line !== '');

    const whatsappUrl = buildWhatsAppUrl(summary);
    setWaLink(whatsappUrl);
    // Ouvert dans le geste utilisateur pour éviter le blocage des fenêtres surgissantes.
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');

    setStatus('sending');
    setFormError('');

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = String(serverMessage).toLowerCase().includes('spam');

      if (!response.ok || parsed?.code !== 'OK' || isSpam) {
        setStatus('error');
        setFormError(
          String(serverMessage || '').trim() ||
            "L'inscription n'a pas pu être envoyée. Merci de réessayer ou de nous appeler.",
        );
        return;
      }

      setStatus('success');
      form.reset();
      onProgramChange('');
    } catch {
      setStatus('error');
      setFormError('Une erreur réseau est survenue. Merci de réessayer ou de nous appeler.');
    }
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
      <div className="flex items-center gap-3">
        <span className="h-px w-8 bg-primary-500" />
        <span className="label-mono text-primary-600">Inscription</span>
      </div>
      <h2 className="mt-4 font-heading text-3xl font-semibold uppercase leading-tight text-foreground-950 md:text-4xl">
        Réserver une place
      </h2>
      <p className="mt-3 text-sm leading-relaxed text-foreground-600">
        Remplissez ce formulaire : votre demande part directement sur le WhatsApp de notre centre,
        et nous revenons vers vous sous 48 h ouvrées avec les dates de session et les modalités
        d’inscription.
      </p>

      {status === 'success' ? (
        <div className="mt-6 flex items-start gap-4 rounded-md border border-accent-300 bg-accent-50 p-5">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-accent-500 text-foreground-950">
            <i className="ri-check-line" />
          </span>
          <div>
            <p className="text-sm font-semibold text-accent-900">Inscription transmise.</p>
            <p className="mt-1 text-xs leading-relaxed text-accent-800">
              Merci pour votre demande. Notre centre de formation vous contacte pour l’entretien de
              positionnement. Pour une question urgente, appelez le {company.phone}.
            </p>
            {waLink ? (
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground-950 transition-colors hover:bg-accent-600"
              >
                <i className="ri-whatsapp-line text-base" />
                Renvoyer sur WhatsApp
              </a>
            ) : null}
          </div>
        </div>
      ) : null}

      {status === 'error' && formError ? (
        <div className="mt-6 flex items-start gap-3 rounded-md border border-primary-300 bg-primary-50 p-4">
          <i className="ri-error-warning-line mt-0.5 text-primary-600" />
          <p className="text-xs leading-relaxed text-primary-900">{formError}</p>
        </div>
      ) : null}

      <form
        id="sgsctic-formation-form"
        data-readdy-form
        onSubmit={handleSubmit}
        className="mt-7 space-y-4"
        noValidate
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="min-w-0">
            <label
              htmlFor="full_name"
              className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground-600"
            >
              Nom complet *
            </label>
            <input
              id="full_name"
              name="full_name"
              type="text"
              required
              maxLength={100}
              placeholder="Votre nom"
              className="box-border w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
            />
          </div>
          <div className="min-w-0">
            <label
              htmlFor="email"
              className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground-600"
            >
              E-mail *
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              maxLength={120}
              placeholder="vous@exemple.com"
              className="box-border w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="min-w-0">
            <label
              htmlFor="phone"
              className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground-600"
            >
              Téléphone
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              maxLength={30}
              placeholder="+237 ..."
              className="box-border w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
            />
          </div>
          <div className="min-w-0">
            <label
              htmlFor="organisation"
              className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground-600"
            >
              Entreprise / structure
            </label>
            <input
              id="organisation"
              name="organisation"
              type="text"
              maxLength={100}
              placeholder="Facultatif"
              className="box-border w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="relative min-w-0">
            <label
              htmlFor="program"
              className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground-600"
            >
              Parcours souhaité *
            </label>
            <select
              id="program"
              name="program"
              required
              value={selectedProgram}
              onChange={(event) => onProgramChange(event.target.value)}
              className="box-border w-full appearance-none rounded-md border border-background-300 bg-background-50 px-4 py-3 pr-10 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
            >
              <option value="">Sélectionner un parcours</option>
              {trainingPrograms.map((program) => (
                <option key={program.slug} value={program.title}>
                  {program.title}
                </option>
              ))}
              <option value="Parcours sur mesure / intra-entreprise">
                Parcours sur mesure / intra-entreprise
              </option>
            </select>
            <i className="ri-arrow-down-s-line pointer-events-none absolute bottom-3.5 right-3 text-lg text-foreground-500" />
          </div>
          <div className="relative min-w-0">
            <label
              htmlFor="experience"
              className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground-600"
            >
              Niveau actuel
            </label>
            <select
              id="experience"
              name="experience"
              defaultValue=""
              className="box-border w-full appearance-none rounded-md border border-background-300 bg-background-50 px-4 py-3 pr-10 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
            >
              <option value="">Sélectionner</option>
              <option value="Débutant — aucune expérience">Débutant — aucune expérience</option>
              <option value="Déjà en atelier (1 à 3 ans)">Déjà en atelier (1 à 3 ans)</option>
              <option value="Confirmé (3 ans et plus)">Confirmé (3 ans et plus)</option>
              <option value="Entreprise — formation d’une équipe">
                Entreprise — formation d’une équipe
              </option>
            </select>
            <i className="ri-arrow-down-s-line pointer-events-none absolute bottom-3.5 right-3 text-lg text-foreground-500" />
          </div>
        </div>

        <div className="min-w-0">
          <label
            htmlFor="message"
            className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground-600"
          >
            Votre projet de formation
          </label>
          <textarea
            id="message"
            name="message"
            rows={4}
            maxLength={500}
            placeholder="Disponibilités, objectif professionnel, nombre de participants…"
            className="box-border w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
          />
          <p className="mt-1 text-[10px] uppercase tracking-[0.14em] text-foreground-400">
            500 caractères maximum
          </p>
        </div>

        <input
          type="text"
          name="contact_alt"
          id="contact_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="form-alt-field"
        />

        <button
          type="submit"
          disabled={status === 'sending'}
          className="btn-shine flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-4 text-sm font-semibold uppercase tracking-[0.1em] text-background-50 transition-colors duration-300 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {status === 'sending' ? 'Envoi en cours…' : 'Envoyer mon inscription'}
          <i className="ri-send-plane-line text-base" />
        </button>
        <p className="flex items-start gap-2 text-[10px] leading-relaxed text-foreground-400">
          <i className="ri-whatsapp-line mt-0.5 text-sm text-accent-600" />
          <span>
            Votre inscription est enregistrée ici puis s’ouvre automatiquement dans WhatsApp pour
            être transmise directement à notre équipe formation.
          </span>
        </p>
      </form>

      <div className="mt-5 border-t border-background-200 pt-5">
        <a
          href={buildWhatsAppUrl([
            'Bonjour SGSCTIC, je souhaite des informations sur vos formations.',
          ])}
          target="_blank"
          rel="noopener noreferrer"
          className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-5 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-foreground-800 transition-colors hover:border-accent-400 hover:bg-accent-50 hover:text-accent-900"
        >
          <i className="ri-whatsapp-line text-base text-accent-600" />
          Écrire directement sur WhatsApp
        </a>
      </div>
    </div>
  );
}